export const users = {
  navigation: {
    forCreators: "For Creators",
    login: "Login",
  },
  hero: {
    badge: "Customer Account",
    title: "Already Purchased from a Creator?",
    description:
      "Log in to your account to access your purchased content, appointments, and messages. All your purchases in one place.",
    loginCta: "Log In",
    noAccount: "Don't have an account yet?",
    signUpLink: "Sign up here",
    features: [
      "Your Purchases",
      "Your Appointments",
      "Your Content",
      "Your Messages",
    ],
    featuresDesc: [
      "View all your purchases and download invoices",
      "Manage your bookings and appointments",
      "Access your digital content",
      "Read and send messages",
    ],
  },
  afterLogin: {
    title: "What Can You Access After Login?",
    description:
      "Everything in your account: purchases, appointments, content, and messages",
    items: [
      {
        title: "Purchased Content",
        description:
          "All your digital content in one place - e-books, courses, templates - downloadable and always accessible",
      },
      {
        title: "Purchase History",
        description:
          "View all your purchases, prices, dates, and download invoices in PDF format",
      },
      {
        title: "Booked Appointments",
        description:
          "Manage your consultations, workshops, and meetings in a clear calendar view",
      },
      {
        title: "Ordered Videos",
        description:
          "Track the status of your personalized video requests and download completed videos",
      },
      {
        title: "Message Threads",
        description:
          "Read and send messages directly to creators through a chat interface",
      },
    ],
  },
  howItWorks: {
    title: "How Does It Work?",
    description: "Connect with your favorite creators in three simple steps",
    steps: [
      {
        title: "Browse Creators",
        description:
          "Discover creators and see what content and services they offer. Follow your favorites to stay updated on new offerings.",
      },
      {
        title: "Choose and Purchase",
        description:
          "Find the content or service that interests you and pay securely. We support multiple payment methods.",
      },
      {
        title: "Enjoy the Content",
        description:
          "Get instant access to digital content, manage appointments in your calendar. Everything in one place, simple.",
      },
    ],
  },
  benefits: {
    title: "What Do You Get?",
    description:
      "Unique experiences and content directly from your favorite creators",
    items: [
      {
        title: "Exclusive Content",
        description:
          "Access special digital content you won't find anywhere else - e-books, courses, tutorials, and much more.",
      },
      {
        title: "Personal Meetings",
        description:
          "Book individual consultations or group workshops with your favorite creators and learn directly from them.",
      },
      {
        title: "Personalized Videos",
        description:
          "Request personalized video messages for special occasions or inspiration from your favorite creators.",
      },
      {
        title: "Direct Connection",
        description:
          "Chat with creators, ask questions, and get answers directly from those you follow.",
      },
      {
        title: "Special Offers",
        description:
          "Perks, discounts, and exclusive packages only for fans purchasing through the platform.",
      },
      {
        title: "Everything in One Place",
        description:
          "Manage your purchases, appointments, content, and messages on a single central interface.",
      },
    ],
  },
  features: {
    title: "Why Choose Us?",
    description: "The best experience for connecting with creators",
    items: [
      {
        title: "Secure Payment",
        description:
          "All transactions are encrypted and secure. We support multiple payment methods.",
      },
      {
        title: "Buyer Protection",
        description:
          "All your purchases are protected. If there's any issue, we'll help resolve it.",
      },
      {
        title: "Instant Access",
        description:
          "Get immediate access to digital content right after purchase.",
      },
      {
        title: "Quality Content",
        description:
          "We verify all creators to ensure you only receive high-quality content.",
      },
      {
        title: "Easy Management",
        description:
          "Intuitive interface where you can easily find what you're looking for and manage your purchases.",
      },
      {
        title: "24/7 Support",
        description:
          "Our customer service is always available if you need assistance.",
      },
    ],
  },
  faq: {
    title: "Frequently Asked Questions",
    items: [
      {
        question: "How can I purchase content?",
        answer:
          "Browse your favorite creator's profile, select the content or service you're interested in, and click the 'Purchase' button. After payment, you'll get instant access to digital content, or your appointment will appear in your calendar.",
      },
      {
        question: "What payment methods do you accept?",
        answer:
          "We support credit cards (Visa, Mastercard), bank transfers, and other popular online payment methods. All transactions are secure and encrypted.",
      },
      {
        question: "Can I get a refund?",
        answer:
          "For digital content, you can request a refund within 14 days of purchase if you haven't opened the content. For services, the creator's refund policy applies.",
      },
      {
        question: "How do I access my purchased content?",
        answer:
          "After logging in, you'll find all your purchased content in the 'My Purchases' menu. Digital products can be immediately downloaded or viewed.",
      },
      {
        question: "What happens if I cancel an appointment?",
        answer:
          "You can cancel appointments free of charge at least 24 hours before the scheduled time. You can do this in the 'My Appointments' menu. Cancellation policies may vary by creator.",
      },
      {
        question: "How can I follow my favorite creators?",
        answer:
          "There's a 'Follow' button on every creator's profile. When you follow someone, you'll receive notifications about their new content and offers.",
      },
      {
        question: "Is it safe to provide my information?",
        answer:
          "Yes, we store all data encrypted and transmit it only over secure connections. We don't share your data with third parties for marketing purposes.",
      },
      {
        question: "Can I get an invoice for my purchases?",
        answer:
          "Yes, we automatically issue an invoice for every purchase, which you can download in PDF format from the 'My Purchases' menu.",
      },
    ],
  },
  newsletter: {
    title: "Subscribe to Our Newsletter",
    description:
      "Be the first to know about new creators, special offers, and exclusive content!",
    placeholder: "Enter your email address",
    cta: "Subscribe",
    success: "Thank you! You've successfully subscribed to our newsletter!",
  },
  footer: {
    description:
      "The platform where you can connect directly with your favorite creators.",
    quickLinks: "Quick Links",
    forCreators: "For Creators",
    login: "Login",
    followUs: "Follow Us",
    copyright: "© 2025 Bespire. All rights reserved.",
  },
  creatorHighlights: {
    title: "Exclusive Creator Content",
    subtitle: "Discover the latest content from the creators you follow in your user account",
    viewButton: "View",
  },
  testimonials: {
    title: "What People Say About Us",
    description: "Our customers' opinions about the platform and the creators",
    items: [
      {
        name: "Anna Kiss",
        role: "Yoga practitioner",
        content: "It was very easy to book my first class. Everything is in one place!",
      },
      {
        name: "Peter Nagy",
        role: "Course buyer",
        content: "The digital content was available immediately. I'm very satisfied.",
      },
      {
        name: "Bela Kovacs",
        role: "Coach client",
        content: "I could quickly talk to my mentor through the chat interface. Super!",
      },
    ],
  },
  getStarted: {
    title: "Ready to Get Started?",
    description: "Join our community and enjoy exclusive content!",
    button: "Register",
  },
  login: {
    title: "Login",
    subtitle: "Sign in to your account",
    googleButton: "Sign in with Google",
    orDivider: "or",
    emailPlaceholder: "Email address",
    passwordPlaceholder: "Password",
    forgotPassword: "Forgot password?",
    loadingButton: "Signing in...",
    submitButton: "Sign In",
    registerPrompt: "Don't have an account yet?",
    registerLink: "Sign up here",
    successToast: {
      title: "Login successful",
      description: "Welcome back!",
    },
    errorToast: {
      title: "Error occurred",
      description: "Please check your credentials!",
    },
  },
};
